import React from "react";

import invariant from "tiny-invariant";

import { getThumbnailImageCoords } from "./utils";

// NOTE: These should be kept in sync with their equivalents in
// apps/meeting_recorder/api/thumbnails.py
const THUMBNAIL_WIDTH = 144;
const THUMBNAIL_HEIGHT = 81;
const THUMBNAIL_SPRITE_IMAGE_GRID_WIDTH = 24;

export function PreviewThumbnail({
	thumbnailSrc,
	className,
	width = THUMBNAIL_WIDTH,
	height = THUMBNAIL_HEIGHT,
	halfsize = false,
}: {
	thumbnailSrc: string | undefined;
	width?: number;
	height?: number;
	className?: string;
	halfsize?: boolean;
}) {
	const imgStyle = React.useMemo(() => {
		if (!thumbnailSrc) return {};
		const coords = getThumbnailImageCoords(thumbnailSrc);
		invariant(coords, "Thumbnail image coords should be defined");
		const [x, y, w] = coords;

		let sizeRatio = w / width;
		let left = `-${x / sizeRatio}px`;

		// if we're using a halfsize thumbnail, we need to adjust the size ratio and where the thumbnail is
		// positioned along the x axis
		if (halfsize) {
			sizeRatio /= 2;
			left = `-${x / sizeRatio + width / 2}px`;
		}

		return {
			position: "absolute" as const,
			left: left,
			top: `-${y / sizeRatio}px`,
			width: `${(THUMBNAIL_SPRITE_IMAGE_GRID_WIDTH * w) / sizeRatio}px`,
		};
	}, [thumbnailSrc, width, halfsize]);

	return (
		<div
			className={className}
			style={{ width: `${width}px`, height: `${height}px`, overflow: "hidden", position: "relative" }}
		>
			<img src={thumbnailSrc} style={imgStyle} />
		</div>
	);
}
